import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';

import { Field, Fields, reduxForm } from 'redux-form';

import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import DateRangeFields from 'components/fields/DateRangeFields';
import AutosuggestField from 'components/fields/AutosuggestField';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import orderStates from 'config/orderStates';

import { requestSearchUsers } from 'redux/modules/users';
import {
  requestFetchPatients,
  requestSearchPatients,
} from 'redux/modules/patients';
import { requestFetchUserNotificationSettings } from 'redux/modules/userNotificationSettings';
import { accountLoginFail } from 'redux/modules/account';

const styles = theme => ({
  root: {

  },
  dateRangeFields: {
    marginRight: -8,
    marginLeft: -8,
  },
  gridItemUser: {
    [theme.breakpoints.down('xs')]: {
      maxWidth: '40%',
      flexBasis: '40%',
    },
  },
});

function onChange(values, dispatch, props, previousValues) {
  if (values['Notification Type Name'] !== values['Selected Notification Type Name'] && values['Notification Type']) {
    props.clearFields(false, false, 'Selected Notification Type Name');
    props.clearFields(false, false, 'Notification Type');
  }

  if (values['User Name'] !== values['Selected User Name'] && values['User ID']) {
    props.clearFields(false, false, 'Selected User Name');
    props.clearFields(false, false, 'User ID');
  }

  if (values['Watcher User Name'] !== values['Selected Watcher User Name'] && values['Watcher User ID']) {
    props.clearFields(false, false, 'Selected Watcher User Name');
    props.clearFields(false, false, 'Watcher User ID');
  }

  if (values['Patient Name'] !== values['Selected Patient Name'] && values['Patient ID']) {
    props.clearFields(false, false, 'Selected Patient Name');
    props.clearFields(false, false, 'Patient ID');
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Notification Type Name'] && values['Notification Type Name'] !== values['Selected Notification Type Name']) {
    errors['Notification Type Name'] = 'Invalid Type';
  }

  if (values['Date From'] && !errors['Date From']) {
    errors['Date From'] = validate.date(values['Date From']);
  }

  if (values['Date To'] && !errors['Date To']) {
    errors['Date To'] = validate.date(values['Date To']);
  }

  if (values['Date From']
    && !validate.date(values['Date From'])
    && values['Date To']
    && !validate.date(values['Date To'])
    && moment(values['Date To'], dateFormat).isBefore(moment(values['Date From'], dateFormat))) {
    errors['Date To'] = 'Date To should be after Date From';
  }

  return errors;
};

const NotificationsFilterForm = ({
  handleSubmit,
  dateRangeMin,
  dateRangeMax,
  account,
  change,
  dispatch,
  className,
  width,
  classes,
}) => (
  <form
    className={classNames(classes.root, className)}
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    <Grid
      container
      wrap={width === 'xs' ? 'wrap' : 'nowrap'}
      justify="space-between"
      spacing={16}
    >
      <Grid item>
        <Fields
          className={classes.dateRangeFields}
          id="Date of Creation"
          names={[
            'Date of Creation From',
            'Date of Creation To',
          ]}
          placeholder="Date"
          component={DateRangeFields}
          label="Date of Creation"
          fullWidth
          size="sm"
          variant="icon"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Field
          id="Patient Name"
          name="Patient Name"
          component={AutosuggestField}
          placeholder="Patient Name"
          fullWidth
          renderSuggestionContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          size="sm"
          listWidth="auto"
          onSuggestionSelected={(suggestion) => {
            change('Patient ID', suggestion['Record Number']);
            change('Selected Patient Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
            return `${suggestion['First Name']} ${suggestion['Last Name']}`;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchPatients({
                filters: [
                  ...value.length > 0 && !/^\d+$/.test(value)
                    ? [{
                      name: 'Full Name',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...value.length > 0 && /^\d+$/.test(value)
                    ? [{
                      name: 'Record Number',
                      operation: 'eq',
                      val: value,
                    }] : [],
                  {
                    name: 'Patient Status',
                    operation: 'eq',
                    val: true,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
              : requestFetchPatients({
                  limit,
                  offset,
                }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid className={classes.gridItemUser} item xs={5} sm={3}>
        <Field
          id="User Name"
          name="User Name"
          component={AutosuggestField}
          placeholder="Assignee"
          fullWidth
          renderSuggestionContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          size="sm"
          listWidth="auto"
          onSuggestionSelected={(suggestion) => {
            change('User ID', suggestion.ID);
            change('Selected User Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
            return `${suggestion['First Name']} ${suggestion['Last Name']}`;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: true,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
              : requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: true,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Field
          name="Watcher User Name"
          id="Watcher User Name"
          component={AutosuggestField}
          placeholder="Watcher"
          fullWidth
          renderSuggestionContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          size="sm"
          listWidth="auto"
          onSuggestionSelected={(suggestion) => {
            change('Watcher User ID', suggestion.ID);
            change('Selected Watcher User Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
            return `${suggestion['First Name']} ${suggestion['Last Name']}`;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: true,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
              : requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: true,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Field
          name="Notification Type Name"
          id="Notification Type Name"
          component={AutosuggestField}
          placeholder="Type"
          fullWidth
          renderSuggestionContent={suggestion => suggestion.Description}
          renderInputContent={suggestion => suggestion.Description}
          size="sm"
          listWidth="auto-right"
          onSuggestionSelected={(suggestion) => {
            change('Notification Type', suggestion.ID);
            change('Selected Notification Type Name', suggestion.Description);
            return suggestion.Description;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            offset > 0
              ? new Promise(resolve => resolve([]))
              : requestFetchUserNotificationSettings().then(res =>
                value.length > 0 ? res.data.filter(item => item.Description.indexOf(value) !== -1) : res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
  </form>
);

NotificationsFilterForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  account: PropTypes.shape({
    ID: PropTypes.number,
  }),
  dateRangeMin: PropTypes.string,
  dateRangeMax: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
  }).isRequired,
};

NotificationsFilterForm.defaultProps = {
  dateRangeMin: null,
  dateRangeMax: null,
};

export default reduxForm({
  onChange,
  validate: validateForm,
})(withStyles(styles)(withWidth()(NotificationsFilterForm)));



// WEBPACK FOOTER //
// ./src/components/home/Notifications/NotificationsFilterForm.js